import React, { useCallback } from 'react';
import { ConnectionHandler, graphql, useFragment, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import Button from '@atlaskit/button';
import NotificationIcon from '@atlaskit/icon/core/migration/notification';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { projectNotificationsRemoveButton_personalSettingsNotificationsConfigTable_RemoveButton$key } from '@atlassian/jira-relay/src/__generated__/projectNotificationsRemoveButton_personalSettingsNotificationsConfigTable_RemoveButton.graphql';
import type {
	projectNotificationsRemoveButton_removeMutation,
	projectNotificationsRemoveButton_removeMutation$data,
} from '@atlassian/jira-relay/src/__generated__/projectNotificationsRemoveButton_removeMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { fireOnCompleteErrorAnalytics, fireOnErrorAnalytics } from '../../../../common/utils.tsx';
import { useConnection as useConnectionId } from '../../../../services/project-connection/index.tsx';
import messages from './messages.tsx';

export const PROJECT_NOTIFICATIONS_REMOVED_FLAG_ID = 'project-notifications-removed-flag';

export const RemoveButton = ({
	project,
}: {
	project: projectNotificationsRemoveButton_personalSettingsNotificationsConfigTable_RemoveButton$key;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const connectionId = useConnectionId();
	const cloudId = useCloudId();
	const [projectIds, setProjectIds] = useQueryParam('projectIds');

	const { showErrorFlag } = useShowErrorFlag();
	const { formatMessage } = useIntl();

	const projectData =
		useFragment<projectNotificationsRemoveButton_personalSettingsNotificationsConfigTable_RemoveButton$key>(
			graphql`
				fragment projectNotificationsRemoveButton_personalSettingsNotificationsConfigTable_RemoveButton on JiraNotificationProjectPreferences {
					__id
					id @required(action: THROW)
					project @required(action: THROW) {
						id @required(action: THROW)
						name @required(action: THROW)
					}
				}
			`,
			project,
		);
	const [commit] = useMutation<projectNotificationsRemoveButton_removeMutation>(graphql`
		mutation projectNotificationsRemoveButton_removeMutation(
			$cloudId: ID!
			$input: JiraDeleteProjectNotificationPreferencesInput!
		) {
			jira {
				deleteProjectNotificationPreferences(cloudId: $cloudId, input: $input)
					@optIn(to: "JiraNotificationPreference") {
					success
					errors {
						message
					}
					projectPreferences {
						id
					}
				}
			}
		}
	`);
	const onClick = useCallback(() => {
		const projectId = projectData.project.id;
		const projectName = projectData.project.name;
		const projectIdArray = (projectIds || '')?.split(',').filter((id) => id);
		const isProjectIdInArray = projectIdArray.includes(projectId);
		const updater = (
			store: RecordSourceSelectorProxy<projectNotificationsRemoveButton_removeMutation$data>,
		) => {
			const connection = store.get(connectionId);
			if (connection != null) {
				ConnectionHandler.deleteNode(connection, projectData.__id);
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const projectCount = (connection.getValue('totalCount') as number) || 0;

				connection.setValue(projectCount - 1, 'totalCount');
			}
		};
		commit({
			variables: {
				cloudId,
				input: {
					projectId: projectData.project.id,
				},
			},
			optimisticUpdater: updater,
			updater,
			onError: (error) => {
				showErrorFlag();
				if (isProjectIdInArray) {
					setProjectIds(projectIdArray.join(','));
				}
				fireOnErrorAnalytics(error, 'Remove', 'project');
			},
			onCompleted: (response) => {
				const errors = response?.jira?.deleteProjectNotificationPreferences?.errors;
				if (errors && errors?.length > 0) {
					showErrorFlag();
					if (isProjectIdInArray) {
						setProjectIds(projectIdArray.join(','));
					}
					fireOnCompleteErrorAnalytics(errors, 'Remove', 'project');
				} else {
					showFlag({
						id: PROJECT_NOTIFICATIONS_REMOVED_FLAG_ID,
						type: 'info',
						isAutoDismiss: true,
						title: formatMessage(messages.projectNotificationsRemoveUndoFlagTitle, {
							projectName,
						}),
						icon: <NotificationIcon spacing="spacious" label="" />,
						actions: [],
						testId:
							'personal-settings-notifications-config-table.ui.project-notifications-config.project-notifications-config-table.project-notifications-undo-flag',
						messageId:
							'personal-settings-notifications-config-table.ui.project-notifications-config.project-notifications-config-table.project-notifications-remove-button.show-flag.info',
						messageType: 'transactional',
					});
					fireUIAnalytics(
						createAnalyticsEvent({}),
						'flag viewed',
						'removeProjectConfigurationFlagViewed',
					);
					fireTrackAnalytics(createAnalyticsEvent({}), 'projectNotifications deleted');
				}
			},
		});
		if (isProjectIdInArray) {
			setProjectIds(projectIdArray.filter((id) => id !== projectId).join(','));
		}
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'projectConfigurationRemoveClicked',
		);
	}, [
		projectData.project.id,
		projectData.project.name,
		projectData.__id,
		projectIds,
		commit,
		cloudId,
		createAnalyticsEvent,
		connectionId,
		showErrorFlag,
		setProjectIds,
		showFlag,
		formatMessage,
	]);

	return (
		<Button
			onClick={onClick}
			testId={`personal-settings-notifications-config-table.ui.project-notifications-config.project-notifications-config-table.project-notifications-remove-button-${projectData?.project?.id}`}
		>
			{formatMessage(messages.projectNotificationsRemoveButton)}
		</Button>
	);
};
